import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "./api";
import { AppearanceProvider } from "./appearance";
import { ChartSettingsProvider } from "./chartSettings";
import { Dashboard } from "./components/Dashboard";
import { ManageTrades } from "./components/ManageTrades";
import { NewTrade } from "./components/NewTrade";
import { PositionSizer } from "./components/PositionSizer";
import { Positions } from "./components/Positions";
import { Settings } from "./components/Settings";
import { Summary } from "./components/Summary";
import { CARD_PARTS } from "./cardParts";
import { ColumnPicker } from "./components/ColumnPicker";
import { TradeCard } from "./components/TradeCard";
import { rupee, signClass } from "./format";
import type {
  Rule,
  StrategyOption,
  Trade,
  Tradebook,
  TradeCardSettings,
} from "./types";

type Tab =
  | "dashboard"
  | "trades"
  | "positions"
  | "manage"
  | "sizer"
  | "summary"
  | "settings";

const TAB_LABELS: Record<Tab, string> = {
  dashboard: "Dashboard",
  trades: "Trades",
  positions: "Open Positions",
  manage: "Manage",
  sizer: "Sizer",
  summary: "Summary",
  settings: "Settings",
};

const BOOK_KEY = "tj.activeBook";

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  // Opens on the overview rather than the trade list: the first question on
  // arriving is how the book is doing, not what to type next.
  const [tab, setTab] = useState<Tab>("dashboard");

  const [books, setBooks] = useState<Tradebook[]>([]);
  const [bookId, setBookId] = useState<number | null>(
    Number(localStorage.getItem(BOOK_KEY)) || null,
  );
  const [trades, setTrades] = useState<Trade[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [strategies, setStrategies] = useState<StrategyOption[]>([]);

  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Only used at phone widths, where the tab bar is a drawer rather than a row.
  const [menuOpen, setMenuOpen] = useState(false);
  // Which parts of a trade card are switched off, kept with the other
  // preferences on the server rather than in this browser.
  const [cardLayout, setCardLayout] = useState<TradeCardSettings>({ hidden: [] });

  useEffect(() => {
    if (authed) api.tradeCard().then(setCardLayout).catch(() => {});
  }, [authed]);

  const saveCardLayout = (hidden: string[]) => {
    setCardLayout({ hidden });
    api.saveTradeCard({ hidden }).catch(() => {});
  };

  useEffect(() => {
    api
      .session()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  const loadBooks = useCallback(async () => {
    const list = await api.tradebooks();
    setBooks(list);
    setBookId((current) => {
      if (current && list.some((b) => b.id === current)) return current;
      return list[0]?.id ?? null;
    });
  }, []);

  const loadRules = useCallback(async () => setRules(await api.rules()), []);
  const loadStrategies = useCallback(
    async () => setStrategies(await api.strategies()),
    [],
  );

  const loadTrades = useCallback(async () => {
    if (!bookId) {
      setTrades([]);
      return;
    }
    setLoading(true);
    try {
      setTrades(
        await api.trades({
          tradebook_id: bookId,
          symbol: search || undefined,
          status_filter: statusFilter || undefined,
        }),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [bookId, search, statusFilter]);

  useEffect(() => {
    if (authed) {
      loadBooks().catch((e) => setError((e as Error).message));
      loadRules().catch((e) => setError((e as Error).message));
      loadStrategies().catch((e) => setError((e as Error).message));
    }
  }, [authed, loadBooks, loadRules, loadStrategies]);

  useEffect(() => {
    if (authed) {
      const timer = setTimeout(loadTrades, search ? 250 : 0);
      return () => clearTimeout(timer);
    }
  }, [authed, loadTrades, search]);

  useEffect(() => {
    if (bookId) localStorage.setItem(BOOK_KEY, String(bookId));
  }, [bookId]);

  const book = useMemo(() => books.find((b) => b.id === bookId) ?? null, [books, bookId]);

  const signOut = async () => {
    await api.logout();
    setAuthed(false);
  };

  const replaceTrade = (updated: Trade) =>
    setTrades((list) => list.map((t) => (t.id === updated.id ? updated : t)));

  if (authed === null) return <div className="page muted">Loading…</div>;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  // Inside the auth check: the settings endpoint needs a session, and fetching
  // it before login would just 401 on every page load.
  return (
    <AppearanceProvider>
    <ChartSettingsProvider>
      <header className="topbar">
        <div className="brand">
          Trading<span>Journal</span>
        </div>

        {/* Shown only on phones; the row of tabs below is hidden there. */}
        <button
          className="ghost menu-toggle"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className="menu-icon">{menuOpen ? "\u2715" : "\u2630"}</span>
          <span className="menu-current">{TAB_LABELS[tab]}</span>
        </button>

        <nav className={`tabs${menuOpen ? " open" : ""}`}>
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
            <button
              key={t}
              className={tab === t ? "active" : ""}
              onClick={() => {
                setTab(t);
                setMenuOpen(false);
              }}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
          {/* Inside the drawer on phones, where the header has no room for it.
              The desktop header keeps its own copy, hidden here. */}
          <button className="menu-signout" onClick={signOut}>
            Sign out
          </button>
        </nav>

        <div className="book-picker">
          {book && (
            <span>
              Balance{" "}
              <strong className={signClass(book.realized_pnl)}>
                {rupee(book.current_balance, 0)}
              </strong>
            </span>
          )}
          <span className="picker-label">Book:</span>
          <select
            value={bookId ?? ""}
            onChange={(e) => setBookId(Number(e.target.value) || null)}
          >
            {books.length === 0 && <option value="">No tradebooks</option>}
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <button className="ghost header-signout" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      <main className="page">
        {error && (
          <div className="banner error">
            {error}
            <button className="ghost right" onClick={() => setError(null)}>
              Dismiss
            </button>
          </div>
        )}

        {books.length === 0 && tab !== "settings" && (
          <div className="card empty">
            <h3>Create a tradebook to begin</h3>
            <p className="muted">
              A tradebook is one broker account, with its own capital and risk limits.
            </p>
            <button className="primary" onClick={() => setTab("settings")}>
              Go to Settings
            </button>
          </div>
        )}

        {tab === "trades" && book && (
          <>
            <div className="toolbar">
              <div className="field">
                <label>Search Symbol</label>
                <input
                  value={search}
                  placeholder="e.g. GOLDCASE"
                  onChange={(e) => setSearch(e.target.value.toUpperCase())}
                />
              </div>
              <div className="field">
                <label>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <ColumnPicker
                label="Show"
                choices={CARD_PARTS}
                hidden={cardLayout.hidden}
                onChange={saveCardLayout}
              />
              <div className="spacer" />
              <button className="primary" onClick={() => setCreating((v) => !v)}>
                {creating ? "Close form" : "+ New trade"}
              </button>
            </div>

            {creating && (
              <NewTrade
                book={book}
                strategies={strategies}
                onCancel={() => setCreating(false)}
                onCreated={(trade) => {
                  setTrades((list) => [trade, ...list]);
                  setCreating(false);
                  loadBooks();
                }}
              />
            )}

            {loading && <div className="muted">Loading trades…</div>}

            {!loading && trades.length === 0 && !creating && (
              <div className="card empty">
                <h3>No trades yet</h3>
                <p className="muted">
                  Enter your next trade by hand — writing down the stop and the thesis at entry
                  is where the awareness comes from. Bulk import lives in Settings.
                </p>
                <button className="primary" onClick={() => setCreating(true)}>
                  + New trade
                </button>
              </div>
            )}

            {trades.map((trade) => (
              <TradeCard
                key={trade.id}
                trade={trade}
                rules={rules}
                strategies={strategies}
                hidden={cardLayout.hidden}
                onChange={(updated) => {
                  replaceTrade(updated);
                  loadBooks();
                }}
                onDelete={async (id) => {
                  await api.deleteTrade(id);
                  setTrades((list) => list.filter((t) => t.id !== id));
                  loadBooks();
                }}
              />
            ))}
          </>
        )}

        {tab === "dashboard" && book && (
          <Dashboard
            tradebookId={book.id}
            strategies={strategies}
            multipleBooks={books.length > 1}
          />
        )}

        {tab === "positions" && book && (
          <Positions
            tradebookId={book.id}
            rules={rules}
            strategies={strategies}
            multipleBooks={books.length > 1}
            onJournalSaved={(updated) => {
              replaceTrade(updated);
              loadBooks();
            }}
          />
        )}

        {tab === "manage" && book && (
          <ManageTrades
            trades={trades}
            onChanged={(updated) => {
              replaceTrade(updated);
              loadBooks();
            }}
            onDeleted={(id) => {
              setTrades((list) => list.filter((t) => t.id !== id));
              loadBooks();
            }}
          />
        )}

        {tab === "sizer" && book && <PositionSizer book={book} />}

        {tab === "summary" && <Summary trades={trades} rules={rules} book={book} books={books} />}

        {tab === "settings" && (
          <Settings
            books={books}
            rules={rules}
            activeBook={book}
            strategies={strategies}
            onStrategiesChanged={loadStrategies}
            onBooksChanged={() => {
              loadBooks();
              loadTrades();
            }}
            onRulesChanged={loadRules}
          />
        )}
      </main>
    </ChartSettingsProvider>
    </AppearanceProvider>
  );
}

function Login({ onSuccess }: { onSuccess: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.login(password);
      onSuccess();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 401
          ? "Incorrect password."
          : (e as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="card login">
        <div className="brand" style={{ marginBottom: 16 }}>
          Trading<span>Journal</span>
        </div>
        {error && <div className="banner error">{error}</div>}
        <label>Password</label>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <button
          className="primary"
          style={{ width: "100%", marginTop: 14 }}
          disabled={busy || !password}
          onClick={submit}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </div>
    </div>
  );
}
